import { Routes, Route } from '@angular/router';
import { AccessGuard, AuthenticationGuard } from '@cm/ui-modules';
import { provideWorkflowStore } from './modules/workflow/workflow.providers';

// Shared configuration for campaign detail child routes
const CAMPAIGN_DETAIL_CHILDREN: Route[] = [
  {
    path: '',
    redirectTo: 'summary',
    pathMatch: 'full'
  },
  {
    path: 'summary',
    loadComponent: () =>
      import('./modules/campaigns/report/details/campaign-summary/campaign-summary.component').then((m) => m.CampaignSummaryComponent),
  },
  {
    path: 'tasks',
    loadComponent: () =>
      import('./modules/campaigns/report/details/campaign-messages/campaign-messages.component').then((m) => m.CampaignMessagesComponent),
  },
  {
    path: 'activities',
    loadComponent: () =>
      import('./modules/campaigns/report/details/campaign-activities/campaign-activities.component').then((m) => m.CampaignActivitiesComponent),
  },
  {
    path: 'representatives',
    loadComponent: () =>
      import('./modules/campaigns/report/details/campaign-representatives/campaign-representatives.component').then((m) => m.CampaignRepresentativesComponent),
  }
];

// Shared route data for reports section
const REPORTS_ROUTE_DATA = {
  routeName: 'reports',
};

export const ROUTES: Routes = [
  {
    path: '',
    canActivate: [AuthenticationGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        data: {
          routeName: 'dashboard',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/dashboard/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: 'designer',
        data: {
          routeName: 'designer',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'designer',
            },
            loadComponent: () =>
              import('./modules/designer/designer-list/designer-list.component').then(
                (m) => m.DesignerListComponent,
              ),
          },
          {
            path: ':diagramUuid/player',
            data: {
              routeName: 'designer',
            },
            loadComponent: () =>
              import('./modules/diagram/components/diagram-player-route/diagram-player-route.component').then(
                (m) => m.DiagramPlayerRouteComponent,
              ),
          },
          {
            path: ':diagramUuid/:nodeUuid',
            data: {
              routeName: 'designer',
            },
            loadComponent: () =>
              import('./modules/diagram/components/node-editor/node-editor-route/node-editor-route.component').then(
                (m) => m.NodeEditorRouteComponent,
              ),
          },
          {
            path: ':id',
            data: {
              routeName: 'designer',
            },
            loadComponent: () =>
              import('./modules/designer/designer.component').then(
                (m) => m.DesignerComponent,
              ),
          },
          {
            path: ':id/preview',
            data: {
              routeName: 'designer',
            },
            loadComponent: () =>
              import('./modules/designer/designer.component').then(
                (m) => m.DesignerComponent,
              ),
          },
        ],
      },
      {
        path: 'consent',
        data: {
          routeName: 'consent',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/consent/consent.component').then((m) => m.ConsentComponent),
      },
      {
        path: 'privilege',
        data: {
          routeName: 'privilege',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/privilege/privilege.component').then((m) => m.PrivilegeComponent),
      },
      {
        path: 'configuration',
        data: {
          routeName: 'configuration',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/configuration-manager/configuration-manager.component').then(
            (m) => m.ConfigurationManagerComponent,
          ),
      },
      {
        path: 'campaigns',
        canActivate: [AccessGuard],
        data: {
          routeName: 'campaigns',
        },
        loadComponent: () =>
          import('./modules/campaigns/campaign/campaign.component').then(
            (m) => m.CampaignComponent,
          ),
      },
      {
        path: 'campaigns/new',
        canActivate: [AccessGuard],
        data: { routeName: 'campaigns' },
        loadComponent: () =>
          import('./modules/campaigns/campaign-new-redirect/campaign-new-redirect.component').then(
            (m) => m.CampaignNewRedirectComponent,
          ),
      },
      {
        path: 'campaigns/creator',
        canActivate: [AccessGuard],
        data: { routeName: 'campaigns' },
        loadChildren: () =>
          import('./modules/campaign-creator/campaign-creator.routes').then(
            m => m.CAMPAIGN_CREATOR_ROUTES
          ),
      },
      {
        path: 'campaigns/creator/edit/:uuid',
        canActivate: [AccessGuard],
        data: { routeName: 'campaigns' },
        loadChildren: () =>
          import('./modules/campaign-creator/campaign-creator.routes').then(
            m => m.CAMPAIGN_CREATOR_ROUTES
          ),
      },
      {
        path: 'reports',
        canActivate: [AccessGuard],
        data: REPORTS_ROUTE_DATA,
        loadComponent: () =>
          import('./modules/campaigns/report/reports/campaign-report.component').then(
            (m) => m.CampaignReportComponent,
          ),
      },
      {
        path: 'label-statistics',
        canActivate: [AccessGuard],
        data: { routeName: 'label-statistics' },
        loadComponent: () =>
          import('./modules/label-statistics/label-statistics.component').then(
            (m) => m.LabelStatisticsComponent,
          ),
      },
      {
        path: 'presentation-reports',
        canActivate: [AccessGuard],
        data: { routeName: 'presentations' },
        loadComponent: () =>
          import('./modules/presentations/report/presentation-report.component').then(
            (m) => m.PresentationReportComponent,
          ),
        children: [
          {
            path: '',
            data: { routeName: 'presentations' },
            loadComponent: () =>
              import('./modules/presentations/report/all-reports/all-reports.component').then(
                (m) => m.PresentationAllReportsComponent,
              ),
          },
        ],
      },
      {
        path: 'presentation-reports/sessions',
        canActivate: [AccessGuard],
        data: { routeName: 'presentations' },
        loadComponent: () =>
          import('./modules/presentations/report/presentation-report.component').then(
            (m) => m.PresentationReportComponent,
          ),
        children: [
          {
            path: '',
            data: { routeName: 'presentations' },
            loadComponent: () =>
              import('./modules/presentations/report/sessions-page/sessions-page.component').then(
                (m) => m.PresentationSessionsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'presentation-reports/presenters',
        canActivate: [AccessGuard],
        data: { routeName: 'presentations' },
        loadComponent: () =>
          import('./modules/presentations/report/presentation-report.component').then(
            (m) => m.PresentationReportComponent,
          ),
        children: [
          {
            path: '',
            data: { routeName: 'presentations' },
            loadComponent: () =>
              import('./modules/presentations/report/presenters-page/presenters-page.component').then(
                (m) => m.PresentationPresentersPageComponent,
              ),
          },
        ],
      },
      {
        path: 'presentation-reports/participants',
        canActivate: [AccessGuard],
        data: { routeName: 'presentations' },
        loadComponent: () =>
          import('./modules/presentations/report/presentation-report.component').then(
            (m) => m.PresentationReportComponent,
          ),
        children: [
          {
            path: '',
            data: { routeName: 'presentations' },
            loadComponent: () =>
              import('./modules/presentations/report/participants-page/participants-page.component').then(
                (m) => m.PresentationParticipantsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'details',
        canActivate: [AccessGuard],
        data: REPORTS_ROUTE_DATA,
        children: [
          {
            path: ':uuid',
            canActivate: [AccessGuard],
            data: REPORTS_ROUTE_DATA,
            loadComponent: () =>
              import(
                './modules/campaigns/report/details/campaign-detail/campaign-detail.component'
              ).then((m) => m.CampaignDetailComponent),
            children: CAMPAIGN_DETAIL_CHILDREN,
          },
          {
            path: 'id/:id',
            canActivate: [AccessGuard],
            data: REPORTS_ROUTE_DATA,
            loadComponent: () =>
              import(
                './modules/campaigns/report/details/campaign-detail/campaign-detail.component'
              ).then((m) => m.CampaignDetailComponent),
            children: CAMPAIGN_DETAIL_CHILDREN,
          },
        ],
      },
      // Redirect old campaign report URLs to new structure for backward compatibility
      {
        path: 'campaign-reports',
        redirectTo: 'reports',

        pathMatch: 'full',
      },
      {
        path: 'campaign-reports/:uuid',
        redirectTo: 'details/:uuid',
        pathMatch: 'full',
      },
      // Redirect old campaign report URLs to new structure for backward compatibility
      {
        path: 'campaigns/report',
        redirectTo: 'campaign-reports',
        pathMatch: 'full',
      },
      {
        path: 'campaigns/report/:uuid',
        redirectTo: 'campaign-reports/:uuid',
        pathMatch: 'full',
      },
      // Redirect old campaign edit URL to admin campaign edit
      {
        path: 'campaing/edit/:uuid',
        redirectTo: '/admin/campaign/edit/uuid/:uuid',
        pathMatch: 'full',
      },
      {
        path: 'meetings',
        canActivate: [AccessGuard],
        data: {
          routeName: 'meetings',
        },
        children: [
          {
            path: '',
            data: {
              routeName: 'meetings',
            },
            loadComponent: () =>
              import('./modules/meetings/containers/meeting-list/meeting-list.component').then(
                (m) => m.MeetingListComponent,
              ),
            children: [
              {
                path: 'participants',
                data: {
                  routeName: 'meetings',
                },
                loadComponent: () =>
                  import(
                    './modules/meetings/containers/meeting-participants/meeting-participants.component'
                  ).then((m) => m.MeetingParticipantsComponent),
              },
              {
                path: 'events',
                data: {
                  routeName: 'meetings',
                },
                loadComponent: () =>
                  import(
                    './modules/meetings/containers/meeting-events/meeting-events.component'
                  ).then((m) => m.MeetingEventsComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'download',
        canActivate: [AccessGuard],
        data: {
          routeName: 'download',
        },
        children: [
          {
            path: ':platform/:name/:version',
            data: {
              routeName: 'download',
            },
            loadComponent: () =>
              import('./download/containers/mobile-app/mobile-app.component').then(
                (m) => m.MobileAppComponent,
              ),
          },
        ],
      },
      {
        path: 'contacts',
        canActivate: [AccessGuard],
        data: {
          routeName: 'contacts',
        },
        children: [
          {
            path: '',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-list/contact-list.component').then(
                (m) => m.ContactListComponent,
              ),
          },
          {
            path: 'create',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-create/contact-create.component').then(
                (m) => m.ContactCreateComponent,
              ),
          },
          {
            path: 'import',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/import/contact-import.component').then(
                (m) => m.ContactImportComponent,
              ),
          },
          {
            path: 'report',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-report/contact-report.component').then(
                (m) => m.ContactReportComponent,
              ),
          },
          {
            path: 'uuid/:uuid/profile',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-profile/contact-profile.component').then(
                (m) => m.ContactProfileComponent,
              ),
          },
          {
            path: 'uuid/:uuid',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-detail/contact-detail.component').then(
                (m) => m.ContactDetailComponent,
              ),
          },
          {
            path: ':id/profile',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-profile/contact-profile.component').then(
                (m) => m.ContactProfileComponent,
              ),
          },
          {
            path: ':uuid',
            data: {
              routeName: 'contacts',
            },
            loadComponent: () =>
              import('./modules/contact/contact-detail/contact-detail.component').then(
                (m) => m.ContactDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'groups',
        data: {
          routeName: 'groups',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'groups',
            },
            loadComponent: () =>
              import('./modules/group/group-list/group-list.component').then(
                (m) => m.GroupListComponent,
              ),
          },
          {
            path: 'create',
            data: {
              routeName: 'groups',
            },
            loadComponent: () =>
              import('./modules/group/group-create/group-create.component').then(
                (m) => m.GroupCreateComponent,
              ),
          },
          {
            path: 'group-contact-list',
            data: {
              routeName: 'groups',
            },
            loadComponent: () =>
              import('./modules/group/group-contact-list/group-contact-list.component').then(
                (m) => m.GroupContactListComponent,
              ),
          },
          {
            path: 'group-reps-list',
            data: {
              routeName: 'groups',
            },
            loadComponent: () =>
              import('./modules/group/group-rep-list/group-rep-list.component').then(
                (m) => m.GroupRepListComponent,
              ),
          },
        ],
      },
      {
        path: 'segments',
        data: {
          routeName: 'segments',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'segments',
            },
            loadComponent: () =>
              import('./modules/segment/segment-list/segment-list.component').then(
                (m) => m.SegmentListComponent,
              ),
          },
          {
            path: 'create',
            data: {
              routeName: 'segments',
            },
            loadComponent: () =>
              import('./modules/segment/segment-create/segment-create.component').then(
                (m) => m.SegmentCreateComponent,
              ),
          },
          {
            path: 'edit/:segmentId',
            data: {
              routeName: 'segments',
            },
            loadComponent: () =>
              import('./modules/segment/segment-create/segment-create.component').then(
                (m) => m.SegmentCreateComponent,
              ),
          },
          {
            path: 'contacts/:segmentId',
            data: {
              routeName: 'segments',
            },
            loadComponent: () =>
              import('./modules/segment/segment-details/segment-details.component').then(
                (m) => m.SegmentDetailsComponent,
              ),
          },
        ],
      },
      {
        path: 'specializations',
        data: {
          routeName: 'specializations',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'specializations',
            },
            loadComponent: () =>
              import(
                './modules/specialization/specialization-list/specialization-list.component'
              ).then((m) => m.SpecializationListComponent),
          },
          {
            path: 'create',
            data: {
              routeName: 'specializations',
            },
            loadComponent: () =>
              import(
                './modules/specialization/specialization-create/specialization-create.component'
              ).then((m) => m.SpecializationCreateComponent),
          },
          {
            path: 'specialization-contact-list',
            data: {
              routeName: 'specializations',
            },
            loadComponent: () =>
              import(
                './modules/specialization/specialization-contact-list/specialization-contact-list.component'
              ).then((m) => m.SpecializationContactListComponent),
          },
        ],
      },
      {
        path: 'leads',
        data: {
          routeName: 'leads',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'leads',
            },
            loadComponent: () =>
              import('./modules/lead/lead-list/lead-list.component').then(
                (m) => m.LeadListComponent,
              ),
          },
          {
            path: 'create',
            data: {
              routeName: 'leads',
            },
            loadComponent: () =>
              import('./modules/lead/lead-create/lead-create.component').then(
                (m) => m.LeadCreateComponent,
              ),
          },
          {
            path: 'edit/:id',
            data: {
              routeName: 'leads',
            },
            loadComponent: () =>
              import('./modules/lead/lead-edit/lead-edit.component').then(
                (m) => m.LeadEditComponent,
              ),
          },
        ],
      },
      {
        path: 'agreement-forms',
        data: {
          routeName: 'forms',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'forms',
            },
            loadComponent: () =>
              import(
                './modules/agreement-forms/agreement-form-list/agreement-form-list.component'
              ).then((m) => m.AgreementFormListComponent),
          },
          {
            path: 'create/:type',
            data: {
              routeName: 'forms',
            },
            loadComponent: () =>
              import(
                './modules/agreement-forms/agreement-form-editor/agreement-form-editor.component'
              ).then((m) => m.AgreementFormEditorComponent),
          },
          {
            path: 'edit/:id',
            data: {
              routeName: 'forms',
            },
            loadComponent: () =>
              import(
                './modules/agreement-forms/agreement-form-editor/agreement-form-editor.component'
              ).then((m) => m.AgreementFormEditorComponent),
          },
        ],
      },
      {
        path: 'workplaces',
        data: {
          routeName: 'workplaces',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'workplaces',
            },
            loadComponent: () =>
              import('./modules/workplace/workplace-list/workplace-list.component').then(
                (m) => m.WorkplaceListComponent,
              ),
          },
          {
            path: 'create',
            data: {
              routeName: 'workplaces',
            },
            loadComponent: () =>
              import('./modules/workplace/workplace-create/workplace-create.component').then(
                (m) => m.WorkplaceCreateComponent,
              ),
          },
          {
            path: 'edit/:id',
            data: {
              routeName: 'workplaces',
            },
            loadComponent: () =>
              import('./modules/workplace/workplace-edit/workplace-edit.component').then(
                (m) => m.WorkplaceEditComponent,
              ),
          },
          {
            path: 'workplace-contact-list',
            data: {
              routeName: 'workplaces',
            },
            loadComponent: () =>
              import(
                './modules/workplace/workplace-contact-list/workplace-contact-list.component'
              ).then((m) => m.WorkplaceContactListComponent),
          },
        ],
      },
      {
        path: 'batch-job',
        data: {
          routeName: 'batch-job',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/batch-jobs/batch-job-list/batch-job-list.component').then(
            (m) => m.BatchJobListComponent,
          ),
      },
      {
        path: 'migrations',
        data: {
          routeName: 'migrations',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/migrations/migrations-page/migrations-page.component').then(
            (m) => m.MigrationsPageComponent,
          ),
      },
      {
        path: 'custom-fields',
        data: {
          routeName: 'custom-fields',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/custom-field/custom-field-list/custom-field-list.component').then(
            (m) => m.CustomFieldListComponent,
          ),
      },
      {
        path: 'billing',
        data: {
          routeName: 'billing',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/billing-list/billing-list.component').then(
                (m) => m.BillingListComponent,
              ),
          },
          {
            path: 'show/:id',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/billing-detail/billing-detail.component').then(
                (m) => m.BillingDetailComponent,
              ),
          },
          {
            path: 'plan',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/billing-plan/billing-plan.component').then(
                (m) => m.BillingPlanComponent,
              ),
          },
          {
            path: 'plan-list',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/billing-plan-list/billing-plan-list.component').then(
                (m) => m.BillingPlanListComponent,
              ),
          },
          {
            path: 'create-plan',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/create-billing-plan/create-billing-plan.component').then(
                (m) => m.CreateBillingPlanComponent,
              ),
          },
          {
            path: 'edit/:id',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/create-billing-plan/create-billing-plan.component').then(
                (m) => m.CreateBillingPlanComponent,
              ),
          },
          {
            path: 'tenant-edit',
            data: {
              routeName: 'billing',
            },
            loadComponent: () =>
              import('./modules/billing/tenant-edit/tenant-edit.component').then(
                (m) => m.TenantEditComponent,
              ),
          },
        ],
      },
      {
        path: 'ai-usage',
        data: {
          routeName: 'ai-usage',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/ai-usage/ai-usage-page/ai-usage-page.component').then(
            (m) => m.AiUsagePageComponent,
          ),
      },
      {
        path: 'specialization-vocabulary',
        data: {
          routeName: 'specialization-vocabulary',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/specialization-vocabulary/specialization-vocabulary.component').then(
            (m) => m.SpecializationVocabularyComponent,
          ),
      },
      {
        path: 'knowhow',
        data: {
          routeName: 'knowhow',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/knowhow/knowhow.component').then((m) => m.KnowhowComponent),
      },
      {
        path: 'labels',
        data: {
          routeName: 'labels',
        },
        canActivate: [AuthenticationGuard],
        loadComponent: () =>
          import('./modules/labels/labels.component').then((m) => m.LabelsComponent),
      },
      {
        path: 'system-updates',
        data: {
          routeName: 'system-updates',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/system-updates/system-updates-page/system-updates-page.component').then(
            (m) => m.SystemUpdatesPageComponent,
          ),
      },
      {
        path: 'system-updates/admin',
        data: {
          routeName: 'system-updates.manage',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/system-updates/system-updates-admin/system-updates-admin.component').then(
            (m) => m.SystemUpdatesAdminComponent,
          ),
      },
      {
        path: 'approve',
        data: {
          routeName: 'approve',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'review',
          },
          {
            path: 'review',
            data: {
              routeName: 'approve',
            },
            loadComponent: () =>
              import('./modules/approve/review-inbox/review-inbox.component').then(
                (m) => m.ReviewInboxComponent,
              ),
          },
          {
            path: 'review/:reviewId',
            data: {
              routeName: 'approve',
            },
            loadComponent: () =>
              import('./modules/approve/review-workspace/review-workspace.component').then(
                (m) => m.ReviewWorkspaceComponent,
              ),
          },
          {
            path: 'reference-documents',
            data: {
              routeName: 'approve',
            },
            loadComponent: () =>
              import('./modules/approve/reference-documents/reference-documents.component').then(
                (m) => m.ReferenceDocumentsComponent,
              ),
          },
          {
            path: 'policy-rules',
            data: {
              routeName: 'approve',
            },
            loadComponent: () =>
              import('./modules/approve/policy-rules/policy-rules.component').then(
                (m) => m.PolicyRulesComponent,
              ),
          },
        ],
      },
      {
        path: 'medialibrary',
        data: {
          routeName: 'medialibrary',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/media-library/media-library.component').then(
            (m) => m.MediaLibraryComponent,
          ),
      },
      {
        path: 'medialibrary/audit-report',
        canActivate: [AuthenticationGuard],
        loadComponent: () =>
          import('./modules/media-library/components/audit-report-page/audit-report-page.component').then(
            (m) => m.AuditReportPageComponent,
          ),
      },
      {
        path: 'medialibrary/:project',
        data: {
          routeName: 'medialibrary',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/media-library/media-library.component').then(
            (m) => m.MediaLibraryComponent,
          ),
      },
      {
        path: 'video-editor/:mediaAssetUuid',
        data: {
          routeName: 'video-editor',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/video-editor/video-editor.component').then(
            (m) => m.VideoEditorComponent,
          ),
      },
      {
        path: 'team-management',
        data: {
          routeName: 'team-management',
        },
        canActivate: [AccessGuard],
        children: [
          {
            path: '',
            data: {
              routeName: 'team-management',
            },
            loadComponent: () =>
              import('./modules/team-management/team-management.component').then(
                (m) => m.TeamManagementComponent,
              ),
          },
          {
            path: 'profile/:uuid',
            data: {
              routeName: 'team-management',
            },
            loadComponent: () =>
              import('./modules/team-management/components/team-member-profile/team-member-profile.component').then(
                (m) => m.TeamMemberProfileComponent,
              ),
          },
        ],
      },
      {
        path: 'contact-relations/:repUuid',
        data: {
          routeName: 'contact-relations',
        },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/contact-relations/contact-relations.component').then(
            (m) => m.ContactRelationsComponent,
          ),
      },
      {
        path: 'org-structure',
        data: { routeName: 'org-structure' },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/org-structure/org-structure-list/org-structure-list.component').then(
            (m) => m.OrgStructureListComponent,
          ),
      },
      {
        path: 'hierarchy-report',
        data: { routeName: 'hierarchy-report' },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/hierarchy-report/hierarchy-report-page/hierarchy-report-page.component').then(
            (m) => m.HierarchyReportPageComponent,
          ),
      },
      {
        path: 'hierarchy-team-management',
        data: { routeName: 'hierarchy-team-management' },
        canActivate: [AccessGuard],
        loadComponent: () =>
          import('./modules/hierarchy-team-management/hierarchy-team-management.component').then(
            (m) => m.HierarchyTeamManagementComponent,
          ),
      },
      {
        path: 'workflow',
        data: {
          routeName: 'workflow',
        },
        canActivate: [AccessGuard],
        providers: provideWorkflowStore(),
        children: [
          {
            path: '',
            data: {
              routeName: 'workflow',
            },
            loadComponent: () =>
              import('./modules/workflow/workflow-list/workflow-list.component').then(
                (m) => m.WorkflowListComponent,
              ),
          },
          {
            path: ':id',
            data: {
              routeName: 'workflow',
            },
            loadComponent: () =>
              import('./modules/workflow/workflow-editor/workflow-editor.component').then(
                (m) => m.WorkflowEditorComponent,
              ),
          },
          {
            path: ':id/report',
            data: {
              routeName: 'workflow',
            },
            loadComponent: () =>
              import('./modules/workflow/workflow-report/workflow-report.component').then(
                (m) => m.WorkflowReportComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'authenticate',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/authenticate/authenticate.component').then(
            (m) => m.AuthenticateComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/authenticate/containers/process-page/process-page.component').then(
                (m) => m.ProcessPageComponent,
              ),
            children: [
              {
                path: 'set-password',
                loadComponent: () =>
                  import(
                    './modules/authenticate/containers/set-password/set-password.component'
                  ).then((m) => m.SetPasswordComponent),
              },
              {
                path: 'reset-password',
                loadComponent: () =>
                  import(
                    './modules/authenticate/containers/reset-password/reset-password.component'
                  ).then((m) => m.ResetPasswordComponent),
              },
              {
                path: 'login',
                loadComponent: () =>
                  import('./modules/authenticate/containers/login/login.component').then(
                    (m) => m.LoginComponent,
                  ),
              },
              {
                path: 'callback',
                loadComponent: () =>
                  import('./modules/authenticate/containers/oauth-callback/oauth-callback.component').then(
                    (m) => m.OAuthCallbackComponent,
                  ),
              },
              {
                path: 'no-access',
                loadComponent: () =>
                  import('./modules/authenticate/containers/no-access/no-access.component').then(
                    (m) => m.NoAccessComponent,
                  ),
              },
              {
                path: 'logout',
                loadComponent: () =>
                  import('./modules/authenticate/containers/logout/logout.component').then(
                    (m) => m.LogoutComponent,
                  ),
              },
              {
                path: 'failure',
                loadComponent: () =>
                  import('./modules/authenticate/containers/failure/failure.component').then(
                    (m) => m.FailureComponent,
                  ),
              },
              {
                path: '',
                redirectTo: 'login',
                pathMatch: 'full',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'logout',
    redirectTo: 'authenticate/logout',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'authenticate',
  },
];
